/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import {
  fetchData,
  postData,
  deleteData,
  patchData,
} from "@jda/lui-portal-utilities";

import { createRequestObject, getAuthToken } from "../utility/commonUtility";
import {
  ICreateRealmRequest,
  IDeleteRealmRequest,
  IEditRealmRequest,
  IGetAllRealmRequest,
  IGetRealmDetailsRequest,
} from "../models/Realms/RealmContracts";
const { MANAGEMENT_SERVICE_URL, IDAM_SERVICE_URL } = window.env;

export const getAllRealms = async (payload: IGetAllRealmRequest) => {
  const { customerId, subEnterpriseId, pageNo, pageSize, searchString } =
    payload;
  let apiURL = `${MANAGEMENT_SERVICE_URL}/customers/${customerId}/accounts/${subEnterpriseId}/realms?page-number=${
    pageNo || 1
  }&page-size=${pageSize || 50}`;
  apiURL = searchString?.trim().length
    ? apiURL + `&filter-realm-name=${searchString || ""}`
    : apiURL;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then((appsResponse) => {
      return appsResponse;
    })
    .catch((error) => {
      throw error;
    });
};

export const createNewRealm = async (data: ICreateRealmRequest) => {
  const {
    geo,
    displayName,
    name,
    contactEmail,
    customerEnvType,
    instance,
    customerId,
    subEnterpriseId,
  } = data;
  const apiURL = `${MANAGEMENT_SERVICE_URL}/customers/${customerId}/accounts/${subEnterpriseId}/realms`;
  return postData<any>(
    createRequestObject(apiURL),
    { geo, displayName, name, contactEmail, customerEnvType, instance },
    getAuthToken
  )
    .then((appsResponse) => {
      return appsResponse;
    })
    .catch((error) => {
      throw error;
    });
};

export const updateRealm = async (data: IEditRealmRequest) => {
  const { id, displayName, name, customerId, subEnterpriseId } = data;
  const apiURL = `${MANAGEMENT_SERVICE_URL}/customers/${customerId}/accounts/${subEnterpriseId}/realms/${id}`;
  return patchData<any>(
    createRequestObject(apiURL),
    { displayName, name },
    getAuthToken
  )
    .then((appsResponse) => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const deleteRealm = async (data: IDeleteRealmRequest) => {
  const { customerId, subEnterpriseId, id } = data;
  const apiURL = `${MANAGEMENT_SERVICE_URL}/customers/${customerId}/accounts/${subEnterpriseId}/realms/${id}`;
  return deleteData(createRequestObject(apiURL), getAuthToken)
    .then((appsResponse) => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getSelectedRealmDetail = async (
  payload: IGetRealmDetailsRequest
) => {
  const { customerId, subEnterpriseId, id } = payload;
  let apiURL = `${MANAGEMENT_SERVICE_URL}/customers/${customerId}/accounts/${subEnterpriseId}/realms/${id}`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then((appsResponse) => {
      return appsResponse;
    })
    .catch((error) => {
      throw error;
    });
};

export const getRealmEntitlementsRequest = async (payload: any) => {
  const { id, customerId, subEnterpriseId } = payload;
  let apiURL = `${MANAGEMENT_SERVICE_URL}/customers/${customerId}/accounts/${subEnterpriseId}/realms/${id}/entitlements`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then((appsResponse) => {
      return appsResponse;
    })
    .catch((error) => {
      throw error;
    });
};

export const updateEntitlementsForSubEnterprise = async (data: any) => {
  const { id, customerId, subEnterpriseId, entitlements } = data;
  const apiURL = `${MANAGEMENT_SERVICE_URL}/customers/${customerId}/accounts/${subEnterpriseId}/realms/${id}/entitlements`;
  return patchData<any>(createRequestObject(apiURL), entitlements, getAuthToken)
    .then((appsResponse) => {
      return appsResponse;
    })
    .catch((error) => {
      throw error;
    });
};

export const getRealmProvisioningStepsRequest = async (
  customerId: string,
  accountId: string,
  realmId: string
) => {
  const apiURL = `${MANAGEMENT_SERVICE_URL}customers/${customerId}/accounts/${accountId}/realms/${realmId}/provisioningStatus`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then((appsResponse) => {
      return appsResponse;
    })
    .catch((error) => {
      throw error;
    });
};

export const getIdp = async () => {
  const apiURL = `${IDAM_SERVICE_URL}/idp`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then((appsResponse) => {
      return appsResponse;
    })
    .catch((error) => {
      throw error;
    });
};
export const getBYDeliversIdpStatus = async () => {
  const apiURL = `${IDAM_SERVICE_URL}/idp/bySupport/status`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then((appsResponse) => {
      return appsResponse;
    })
    .catch((error) => {
      throw error;
    });
};
export const updateByIdpStatus = async (enabled: boolean) => {
  const apiURL = `${IDAM_SERVICE_URL}/idp/bySupport/status`;
  return patchData<any>(createRequestObject(apiURL), { enabled }, getAuthToken)
    .then((appsResponse) => {
      return appsResponse;
    })
    .catch((error) => {
      throw error;
    });
};
